import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface KpiCell {
  key: string;
  label: string;
  /** The number (already formatted). Ignored while loading or on error. */
  value: ReactNode;
  /** Short context after the value (the manager's "פעילות עכשיו" link text). */
  sub?: ReactNode;
  /** Status meaning of the cell: `waiting` = warning dot. */
  status?: 'waiting' | 'done';
  /** A toggle (the manager's "עובדים עכשיו" → the "פעילות עכשיו" tab): the cell becomes a link-button. */
  pressed?: boolean;
  onToggle?: () => void;
}

interface WorkLogsKpiStripProps {
  cells: KpiCell[];
  state: 'loading' | 'error' | 'ok';
  /** Visually hidden section heading. */
  heading: string;
}

/**
 * The work logs summary (D-159, PRESERVE #3): ONE thin line of plain text, no
 * meters, no boxes — today · week · month · since the start · open logs, and
 * for the OTE manager "עובדים עכשיו" as a link to the "פעילות עכשיו" tab.
 * A failed load shows "—" + "לא נטען", never 0.
 */
export default function WorkLogsKpiStrip({ cells, state, heading }: WorkLogsKpiStripProps) {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} aria-busy={state === 'loading'}>
      <h2 id={headingId} className="sr-only">{heading}</h2>
      <ul className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm text-text-2">
        {cells.map((c) => {
          const value = state === 'loading'
            ? <span className="inline-block h-3.5 w-10 translate-y-0.5 rounded-xs bg-surface-subtle animate-pulse" aria-hidden="true" />
            : state === 'error'
              ? <span className="text-text-3">—</span>
              : <span className="font-semibold tabular-nums text-text">{c.value}</span>;
          const body = (
            <>
              {state === 'ok' && c.status === 'waiting' && <span className="me-1 inline-block size-1.5 rounded-full bg-warning-600 align-middle" aria-hidden="true" />}
              <span>{c.label}</span>{' '}
              {value}
              {state === 'ok' && c.sub && <span className="ms-1 text-accent-soft-text underline">{c.sub}</span>}
            </>
          );
          return (
            <li key={c.key}>
              {c.onToggle && state === 'ok' ? (
                <button type="button" onClick={c.onToggle} aria-pressed={!!c.pressed} className="rounded-xs text-start hover:text-text focus-visible:outline-offset-2">
                  {body}
                </button>
              ) : body}
            </li>
          );
        })}
        {state === 'error' && <li className="text-xs text-text-3">{t('states.notLoaded')}</li>}
      </ul>
    </section>
  );
}
